import {
  type BreakdownSingleDataSet,
  type DataProviderSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../DataProvider/types';
import type { ComparisonBaseline } from '../../PageConfigProvider/types/item';

/**
 * Prefer `share` for legacy composition breakdowns. Fall back to `value` when `share` is absent
 * (e.g. Snowflake TWO_DIMENSION rows that only populate VALUE).
 */
function breakdownComparisonMetric(point: SingleDataPoint): number {
  if (point.share !== null && point.share !== undefined) {
    return point.share;
  }
  if (point.value !== null && point.value !== undefined) {
    return point.value;
  }
  return 0;
}

export type ComparisonTransformationConfig = {
  baseline?: ComparisonBaseline;
};

export const transformComparisonData = (
  data: DataProviderSet,
  config: ComparisonTransformationConfig
): DataProviderSet => {
  const baseline = config.baseline ?? 'first';

  if (data.type === DataType.BREAKDOWN_SINGLE) {
    return transformBreakdownSingleData(data, baseline);
  }

  return data;
};

const transformBreakdownSingleData = (
  data: BreakdownSingleDataSet,
  baseline: ComparisonBaseline
): SingleDataSet => {
  if (data.data.length !== 2) {
    return {
      type: DataType.SINGLE,
      data: [],
    };
  }

  const [entity1, entity2] = data.data;
  if (!entity1 || !entity2) {
    return {
      type: DataType.SINGLE,
      data: [],
    };
  }

  const baselineEntity = baseline === 'second' ? entity2 : entity1;
  const comparisonEntity = baseline === 'second' ? entity1 : entity2;

  const baselineBreakdown = baselineEntity.breakdown;
  const comparisonBreakdown = comparisonEntity.breakdown;

  const allDimensions = new Set<string>();
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  baselineBreakdown.forEach((b) => allDimensions.add(b.dim));
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  comparisonBreakdown.forEach((b) => allDimensions.add(b.dim));

  const transformedData: SingleDataPoint[] = Array.from(allDimensions).map(
    (dim) => {
      const baselinePoint = baselineBreakdown.find((b) => b.dim === dim);
      const comparisonPoint = comparisonBreakdown.find((b) => b.dim === dim);
      const baselineValue = baselinePoint
        ? breakdownComparisonMetric(baselinePoint)
        : 0;
      const comparisonValue = comparisonPoint
        ? breakdownComparisonMetric(comparisonPoint)
        : 0;

      const transformedValue = calculateTransformation(
        baselineValue,
        comparisonValue
      );

      return {
        dim,
        value: transformedValue,
      };
    }
  );

  const sortedData = transformedData.sort(
    (a, b) => (b.value ?? 0) - (a.value ?? 0)
  );

  return {
    type: DataType.SINGLE,
    data: sortedData,
  };
};

const calculateTransformation = (
  baselineValue: number,
  comparisonValue: number
): number => {
  return comparisonValue - baselineValue;
};
